import { Data2 } from '../../../features/data-types'
import { round } from '../../../utils/typeUtils'
import { DeliveryOrder } from '../../deliveries'
import { Item } from '../../operations'
import { SaleOperation, HoardOrder, HoardType, InvoiceMode } from '../state/types'

const QUANTITY_TOLERANCE = 0.000001

export type InvoiceableItem = {
    item: Item
    quantity: number
}

export type InvoiceableItems = {
    items: InvoiceableItem[]
    fullInvoice: boolean
}

export type InvoicedSplit = {
    invoiced: Data2[]
    uninvoiced: Data2[]
}

// Cantidad de un ítem que ya fue facturada (nunca más que la cantidad afectada).
export const getInvoicedQuantity = (item: Item | undefined, quantity: number): number =>
    round(Math.min(quantity, item?.invoiceStats?.invoiced || 0), 6)

// Parte las cantidades afectadas por una devolución o cancelación entre lo que ya tiene
// comprobante fiscal (genera nota de crédito) y lo que nunca se facturó (nota interna).
// Siempre se consume primero lo no facturado: una devolución/cancelación solo llega a la nota de
// crédito cuando supera las unidades que todavía no tenían comprobante.
// - Devolución: el universo es lo entregado (neto de devoluciones anteriores), que se recibe en
//   `deliveredByItem` porque el ítem de la operación no guarda el entregado.
// - Cancelación: el universo es lo activo (cantidad menos lo ya cancelado).
export const splitQuantitiesByInvoicing = (
    operation: SaleOperation,
    itemsData: Data2[],
    mode: 'return' | 'cancel',
    deliveredByItem: Map<string, number> = new Map()
): InvoicedSplit => {
    const invoiced: Data2[] = []
    const uninvoiced: Data2[] = []

    itemsData.forEach(itemData => {
        const itemId = itemData.itemId! as string
        const quantity = (itemData.quantity || 0) as number
        if (quantity <= QUANTITY_TOLERANCE) return

        const item = operation.items.find(operationItem => operationItem.id === itemId)
        const invoicedAvailable = item?.invoiceStats?.invoiced || 0
        const availableQuantity = mode === 'return'
            ? deliveredByItem.get(itemId) || 0
            : Math.max(0, (item?.quantity || 0) - (item?.cancelledQuantity || 0))
        const uninvoicedAvailable = Math.max(0, availableQuantity - invoicedAvailable)

        // Lo que excede lo no facturado se imputa a lo facturado, sin superar lo facturado.
        const invoicedQuantity = round(
            Math.min(Math.max(0, quantity - uninvoicedAvailable), invoicedAvailable),
            6
        )
        const uninvoicedQuantity = round(quantity - invoicedQuantity, 6)

        if (invoicedQuantity > QUANTITY_TOLERANCE) {
            invoiced.push({ ...itemData, itemId, quantity: invoicedQuantity })
        }
        if (uninvoicedQuantity > QUANTITY_TOLERANCE) {
            uninvoiced.push({ ...itemData, itemId, quantity: uninvoicedQuantity })
        }
    })

    return { invoiced, uninvoiced }
}

// Entregado neto por ítem (incluye devoluciones descontadas): suma las órdenes de entrega vigentes.
export const getNetDeliveredByItem = (deliveryOrders: DeliveryOrder[]): Map<string, number> => {
    const deliveredByItem = new Map<string, number>()

    deliveryOrders
        .filter(deliveryOrder => !deliveryOrder.cancelled)
        .forEach(deliveryOrder => (deliveryOrder.items || []).forEach(item => {
            const delivered = item.deliveryStats?.delivered || 0
            deliveredByItem.set(item.itemId, (deliveredByItem.get(item.itemId) || 0) + delivered)
        }))

    return deliveredByItem
}

// Hay productos facturados que todavía no se entregaron: cerrar el acopio dejaría factura sin entrega.
export const hasInvoicedPendingItems = (hoard: HoardOrder, deliveryOrders: DeliveryOrder[]): boolean => {
    const deliveredByItem = getNetDeliveredByItem(deliveryOrders)
    return hoard.items.some(item =>
        (item.invoiceStats?.invoiced || 0) > (deliveredByItem.get(item.id) || 0) + QUANTITY_TOLERANCE
    )
}

// Ítems y cantidades que se facturarían con el modo elegido. En el total de un acopio financiero
// no hay ítems: el comprobante cubre el saldo completo del crédito (`fullInvoice`).
export const getInvoiceableItems = (
    hoard: HoardOrder,
    deliveryOrders: DeliveryOrder[],
    mode: InvoiceMode
): InvoiceableItems => {
    if (hoard.type === HoardType.FINANCIAL && mode === InvoiceMode.TOTAL) {
        return { items: [], fullInvoice: true }
    }

    const deliveredByItem = getNetDeliveredByItem(deliveryOrders)
    const items = hoard.items
        .map(item => {
            const invoiced = item.invoiceStats?.invoiced || 0
            const available = mode === InvoiceMode.DELIVERED
                ? (deliveredByItem.get(item.id) || 0)
                : item.quantity - (item.cancelledQuantity || 0)
            return { item, quantity: available - invoiced }
        })
        .filter(({ quantity }) => quantity > QUANTITY_TOLERANCE)

    return { items, fullInvoice: false }
}
